import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { ChevronLeft, Save, FileText, Mail, Phone, MapPin } from 'lucide-react'
import { toast } from 'sonner'

interface AboutText {
  paragraph1: string
  paragraph2: string
  paragraph3: string
  paragraph4: string
}

interface ContactInfo {
  email: string
  phone: string
  address: string
}

export default function SettingsManager() {
  const { token } = useAuth()
  const [activeTab, setActiveTab] = useState<'about' | 'contact'>('about')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const hasLoaded = useRef(false)
  
  const [aboutText, setAboutText] = useState<AboutText>({
    paragraph1: '',
    paragraph2: '',
    paragraph3: '',
    paragraph4: ''
  })
  
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: '',
    phone: '',
    address: ''
  })

  // Carregar apenas uma vez quando o token estiver disponível
  useEffect(() => {
    if (token && !hasLoaded.current) {
      hasLoaded.current = true
      loadSettings()
    } else if (!token && !isLoading) {
      setIsLoading(false)
    }
  }, [token])

  const loadSettings = async () => {
    if (!token) {
      console.log('[SettingsManager] Sem token, abortando loadSettings')
      return
    }
    
    setIsLoading(true)
    
    try {
      // Carregar texto do Sobre
      const aboutResponse = await fetch(`/api/admin/settings/about_text`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (aboutResponse.ok) {
        const data = await aboutResponse.json()
        if (data.value) {
          setAboutText(data.value)
        } else {
          setAboutText({
            paragraph1: 'O Projeto tem por objeto parceria público-privada (PPP), na modalidade de concessão administrativa, para a prestação dos serviços públicos de apoio à visitação, gestão, operação, manutenção e conservação do Parque da Cidade, Parque Jardim das Mangueiras e Parque das Águas, bem como a execução de obras e serviços de engenharia, no município de Porto Velho – RO.',
            paragraph2: 'A proposta busca promover a revitalização, modernização, manutenção e ampliação da infraestrutura dos parques urbanos, garantindo mais qualidade nos espaços públicos, incentivo ao turismo, lazer, esporte, cultura e convivência social para a população.',
            paragraph3: 'Os estudos foram desenvolvidos por equipe multidisciplinar especializada, abrangendo análises operacionais, ambientais, econômico-financeiras e jurídicas, visando assegurar a viabilidade do projeto, a sustentabilidade dos serviços e a melhoria contínua dos espaços urbanos.',
            paragraph4: 'O projeto também prevê mecanismos de transparência, participação social e governança, permitindo que a população acompanhe as etapas de desenvolvimento e futura implementação da parceria público-privada.'
          })
        }
      }

      const contactResponse = await fetch(`/api/admin/settings/contact_info`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (contactResponse.ok) {
        const data = await contactResponse.json()
        if (data.value) {
          setContactInfo(data.value)
        } else {
          setContactInfo({
            email: 'cgppp@portovelho.ro.gov.br',
            phone: '(69) 99204-0403',
            address: 'Prefeitura Municipal de Porto Velho – RO'
          })
        }
      }
    } catch (error) {
      console.error('[SettingsManager] Erro ao carregar configurações:', error)
      toast.error('Erro ao carregar configurações')
    } finally {
      setIsLoading(false)
    }
  }

  const saveAboutText = async () => {
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.')
      return
    }
    
    setIsSaving(true)
    
    try {
      const response = await fetch(`/api/admin/settings/about_text`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ value: aboutText })
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.details || data.error || 'Erro ao salvar')
      }
      
      toast.success('Texto do Sobre salvo com sucesso!')
      window.dispatchEvent(new CustomEvent('settings-updated'))
    } catch (err) {
      const error = err as Error
      console.error('[SettingsManager] Erro ao salvar:', error)
      toast.error(`Erro ao salvar: ${error.message}`)
    } finally {
      setIsSaving(false)
    }
  }

  const saveContactInfo = async () => {
    if (!token) {
      toast.error('Sessão expirada. Faça login novamente.')
      return
    }
    
    setIsSaving(true)
    
    try {
      const response = await fetch(`/api/admin/settings/contact_info`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ value: contactInfo })
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.details || data.error || 'Erro ao salvar')
      }
      
      toast.success('Informações de contato salvas com sucesso!')
      window.dispatchEvent(new CustomEvent('settings-updated'))
    } catch (err) {
      const error = err as Error
      console.error('[SettingsManager] Erro ao salvar:', error)
      toast.error(`Erro ao salvar: ${error.message}`)
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#51A64D] mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando configurações...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <a href="/admin/dashboard" className="text-gray-400 hover:text-gray-600">
            <ChevronLeft size={20} />
          </a>
          <div>
            <h1 className="text-xl font-serif font-bold text-gray-900">Configurações do Site</h1>
            <p className="text-sm text-gray-500">Gerencie os textos e informações do portal</p>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('about')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'about'
                ? 'text-[#51A64D] border-b-2 border-[#51A64D]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText size={16} />
            Sobre o Projeto
          </button>
          <button
            onClick={() => setActiveTab('contact')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'contact'
                ? 'text-[#51A64D] border-b-2 border-[#51A64D]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Mail size={16} />
            Contato
          </button>
        </div>

        {activeTab === 'about' && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-serif font-bold text-lg text-gray-900 mb-4">Texto da seção "Sobre o Projeto"</h2>
            <p className="text-sm text-gray-500 mb-6">
              Este texto aparece na página inicial, na seção "Sobre o Projeto".
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parágrafo 1</label>
                <textarea
                  value={aboutText.paragraph1}
                  onChange={(e) => setAboutText({ ...aboutText, paragraph1: e.target.value })}
                  rows={4}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parágrafo 2</label>
                <textarea
                  value={aboutText.paragraph2}
                  onChange={(e) => setAboutText({ ...aboutText, paragraph2: e.target.value })}
                  rows={4}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parágrafo 3</label>
                <textarea
                  value={aboutText.paragraph3}
                  onChange={(e) => setAboutText({ ...aboutText, paragraph3: e.target.value })}
                  rows={4}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parágrafo 4</label>
                <textarea
                  value={aboutText.paragraph4}
                  onChange={(e) => setAboutText({ ...aboutText, paragraph4: e.target.value })}
                  rows={4}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={saveAboutText}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white rounded-lg hover:bg-[#458f3f] transition-colors disabled:opacity-50"
              >
                {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
                Salvar alterações
              </button>
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-serif font-bold text-lg text-gray-900 mb-4">Informações de Contato</h2>
            <p className="text-sm text-gray-500 mb-6">
              Estas informações aparecem no rodapé e na seção de contato.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Mail size={16} className="text-[#51A64D]" />
                  E-mail
                </label>
                <input
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Phone size={16} className="text-[#51A64D]" />
                  Telefone
                </label>
                <input
                  type="text"
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <MapPin size={16} className="text-[#51A64D]" />
                  Endereço
                </label>
                <input
                  type="text"
                  value={contactInfo.address}
                  onChange={(e) => setContactInfo({ ...contactInfo, address: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm focus:border-[#51A64D] focus:outline-none focus:ring-2 focus:ring-[#51A64D]/20"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={saveContactInfo}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-[#51A64D] text-white rounded-lg hover:bg-[#458f3f] transition-colors disabled:opacity-50"
              >
                {isSaving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
                Salvar alterações
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}